import React, { useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@love-irish/ui-components/dialog"

import { useModalAnalytics } from "@love-irish/analytics"
import { lookupGrammar } from "./grammar_glossary"
import { GrammarSections } from "./grammar_reference_modal"
import IrishLanguageModal from "./irish_language_modal"
import { grammarTermTarget } from "./language_sections"

export interface GrammarTermProps {
  /** The full term name, e.g. "Genitive singular". */
  label: string
  /** Field/glossary key used to look up the rich multi-slide explainer (e.g. "conjugation_class"). */
  termKey?: string | null
  /** Short fallback description, used when there is no glossary entry. */
  description?: string | null
  /** Short fallback example, used when there is no glossary entry. */
  example?: string | null
  children: React.ReactNode
}

/**
 * Wraps a grammar annotation (e.g. "gs.") so clicking it explains the term. When the term is covered
 * by the language modal (gender, cases, conjugation, ...), it opens the full guide at that point so the
 * reader can keep paging through the rest. Otherwise it shows a compact inline explainer (a glossary
 * entry, or the short description/example from the field schema). Falls back to plain children when
 * there is nothing to explain, so it is safe to wrap every annotation. The child keeps its own tooltip.
 */
const GrammarTerm: React.FC<GrammarTermProps> = ({ termKey, label, description, example, children }) => {
  const [open, setOpen] = useState(false)
  useModalAnalytics("grammar_term", open)

  const target = grammarTermTarget(termKey)
  const entry = lookupGrammar(termKey)
  const explainable = Boolean(target || entry || description || example)
  if (!explainable) return <>{children}</>

  return (
    <>
      <button
        type="button"
        aria-label={`What does ${label} mean?`}
        onClick={() => setOpen(true)}
        className="cursor-help border-0 bg-transparent p-0 text-inherit underline decoration-dotted underline-offset-2"
      >
        {children}
      </button>
      {target ? (
        // Open the full language modal at this grammar point (the reader can keep paging from there).
        <IrishLanguageModal
          open={open}
          onOpenChange={setOpen}
          defaultPage={target.page}
          defaultChapterPage={target.chapterPage}
        />
      ) : (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent>
            {entry ? (
              <GrammarSections entry={entry} analyticsName="grammar_term" />
            ) : (
              <>
                <DialogHeader>
                  <DialogTitle>{label}</DialogTitle>
                  {description && <DialogDescription>{description}</DialogDescription>}
                </DialogHeader>
                {example && (
                  <div className="text-sm">
                    <p className="mb-1 font-semibold text-muted-foreground">Example</p>
                    <p>{example}</p>
                  </div>
                )}
              </>
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}

export default GrammarTerm
